import { useLocale as useNextIntlLocale } from 'next-intl';
import { createNavigation } from 'next-intl/navigation';
import { defineRouting } from 'next-intl/routing';
import type { ComponentProps } from 'react';
import React from 'react';
import type { SupportedLanguage } from '@/app/types';
import { LANGUAGE_CONFIG } from './config';

/**
 * URL 决定语言：显式前缀使用对应语言，无前缀使用站点默认语言（全球站 en、国内站 zh）。
 * 关闭浏览器语言检测，避免用户切换英文后，无前缀地址又被重定向到浏览器偏好语言。
 * E2E 托管 preview（`E2E_LOCAL_PREVIEW=1`）保留 `always`，与 `withE2ERouteLocale` 显式前缀一致。
 *
 * `localeCookie: false`：URL 是 locale 的唯一真源（见 proxy.ts），业务不消费 NEXT_LOCALE。
 * 关闭后中间件不再给响应写 Set-Cookie，公开内容页才能被 CloudFront 缓存（带 Set-Cookie 的响应 CDN 默认拒缓）。
 */
export const routing = defineRouting({
  locales: LANGUAGE_CONFIG.supported,
  defaultLocale: LANGUAGE_CONFIG.default,
  localePrefix: process.env.E2E_LOCAL_PREVIEW === '1' ? 'always' : 'as-needed',
  localeDetection: false,
  localeCookie: false,
});

// TOFIX: createSharedPathnamesNavigation is deprecated, use createNavigation instead
const navigation = createNavigation(routing);
const LinkPrimitive = navigation.Link;

export const Link = ({ prefetch = false, ...props }: ComponentProps<typeof LinkPrimitive>) => {
  const dataAttr = { 'data-google-interstitial': 'false' } as const;
  return React.createElement(LinkPrimitive, { ...props, prefetch, ...dataAttr });
};

export const { getPathname, redirect, usePathname, useRouter } = navigation;

interface ParsedLocalePath {
  locale?: SupportedLanguage;
  pathname: string;
}

/**
 * 同时返回路径语言和去掉语言前缀后的路径，供登录回跳等场景复用。
 */
export const parseLocalePath = (href: string): ParsedLocalePath => {
  if (!href.startsWith('/') || href.startsWith('//')) {
    return { locale: undefined, pathname: href };
  }

  const firstSegment = href.slice(1).split(/[/?#]/, 1)[0];
  const locale = routing.locales.find((supportedLocale) => supportedLocale === firstSegment);
  if (!locale) return { locale: undefined, pathname: href };

  const rest = href.slice(locale.length + 1);
  return {
    locale,
    pathname: !rest ? '/' : rest.startsWith('/') ? rest : `/${rest}`,
  };
};

/**
 * 为站内地址补上当前语言的 URL 前缀；已有前缀和外部地址保持原样。
 */
export const getLocalizedHref = (href: string, locale: SupportedLanguage): string => {
  if (!href.startsWith('/') || href.startsWith('//') || parseLocalePath(href).locale) {
    return href;
  }

  return getPathname({ href, locale });
};

// 增加了类型提示的 useLocale
export function useLocale(): SupportedLanguage {
  return useNextIntlLocale() as SupportedLanguage;
}
